import type { EChartsOption } from 'echarts'
import {
  axisTextStyle,
  deltaColor,
  formatCompactNumber,
  formatShare,
  formatSignedShare,
  isFiniteNumber,
  relativeDelta,
  tooltipBase,
  tooltipHtml,
} from './format'
import { rampColor, withAlpha, type ChartTheme } from './palette'

export interface FunnelStep {
  key: string
  label: string
  value: number | null
  /** Значение шага в периоде сравнения. */
  previous?: number | null
  /** Конверсия из совместимой выборки API. null запрещает пересчёт из разных охватов. */
  conversion?: number | null
  previousConversion?: number | null
  cumulativeConversion?: number | null
}

export interface FunnelInput {
  steps: FunnelStep[]
  valueFormat?: (value: number) => string
  /** Подписи рядов для легенды при сравнении. */
  currentLabel?: string
  previousLabel?: string
}

export interface FunnelConversion {
  /** Конверсия из предыдущего шага (0..1); у первого шага — `null`. */
  fromPrevious: number | null
  /** Конверсия из первого шага. */
  fromFirst: number | null
  /** То же в периоде сравнения. */
  previousFromPrevious: number | null
}

/** Конверсии между шагами: из предыдущего и из первого; деление на ноль и пустые — `null`. */
export function funnelConversions(steps: FunnelStep[]): FunnelConversion[] {
  const ratio = (a: number | null | undefined, b: number | null | undefined) =>
    isFiniteNumber(a) && isFiniteNumber(b) && b > 0 ? a / b : null
  const first = steps[0]
  return steps.map((step, index) => ({
    fromPrevious: index === 0 ? null : step.conversion !== undefined ? (isFiniteNumber(step.conversion) ? step.conversion : null) : ratio(step.value, steps[index - 1].value),
    fromFirst: index === 0 ? null : step.cumulativeConversion !== undefined ? (isFiniteNumber(step.cumulativeConversion) ? step.cumulativeConversion : null) : ratio(step.value, first?.value),
    previousFromPrevious: index === 0 ? null : step.previousConversion !== undefined ? (isFiniteNumber(step.previousConversion) ? step.previousConversion : null) : ratio(step.previous, steps[index - 1].previous),
  }))
}

/** Цвет шага на порядковой шкале (порядок шагов виден в цвете). */
export function funnelStepColor(theme: ChartTheme, index: number, count: number): string {
  return rampColor(theme.ordinal, count > 1 ? index / (count - 1) : 0)
}

/**
 * Воронка как горизонтальные столбцы одной базы: длина — объём шага, цвет — порядковая шкала одного оттенка.
 * Подпись справа: объём и конверсия из предыдущего шага. Период сравнения — полупрозрачный столбец тем же
 * цветом за основным, в подсказке — Δ объёма и Δ конверсии.
 */
export function buildFunnelOption(input: FunnelInput, theme: ChartTheme): EChartsOption {
  const steps = input.steps
  const valueFormat = input.valueFormat ?? ((value: number) => formatCompactNumber(value))
  const conversions = funnelConversions(steps)
  const hasCompare = steps.some((step) => isFiniteNumber(step.previous))
  const colors = steps.map((_step, index) => funnelStepColor(theme, index, steps.length))

  const series: any[] = []
  if (hasCompare) {
    series.push({
      id: 'previous',
      name: input.previousLabel ?? 'Период сравнения',
      type: 'bar',
      barMaxWidth: 24,
      barGap: '-100%',
      z: 1,
      silent: true,
      itemStyle: { color: withAlpha(colors[0] ?? theme.ordinal[0], 0.25) },
      data: steps.map((step, index) => ({
        value: isFiniteNumber(step.previous) ? step.previous : '-',
        itemStyle: { color: withAlpha(colors[index], 0.25), borderRadius: [0, 4, 4, 0] },
      })),
    })
  }
  series.push({
    id: 'current',
    name: input.currentLabel ?? 'Текущий период',
    type: 'bar',
    barMaxWidth: 24,
    barGap: '-100%',
    barCategoryGap: '40%',
    z: 2,
    cursor: 'pointer',
    itemStyle: { color: colors[0] ?? theme.ordinal[0] },
    data: steps.map((step, index) => ({
      value: isFiniteNumber(step.value) ? step.value : '-',
      itemStyle: { color: colors[index], borderRadius: [0, 4, 4, 0] },
    })),
    label: {
      show: true,
      position: 'right',
      distance: 8,
      formatter: (params: any) => {
        const step = steps[params.dataIndex]
        const conversion = conversions[params.dataIndex]
        const value = isFiniteNumber(step?.value) ? valueFormat(step.value) : '—'
        return conversion?.fromPrevious !== null && conversion?.fromPrevious !== undefined
          ? `{value|${value}}  {muted|${formatShare(conversion.fromPrevious)} от пред. шага}`
          : `{value|${value}}`
      },
      rich: {
        value: { color: theme.text, fontSize: 12, fontWeight: 600, fontFamily: theme.fontFamily },
        muted: { color: theme.textMuted, fontSize: 11, fontFamily: theme.fontFamily },
      },
    },
  })

  return {
    backgroundColor: 'transparent',
    animationDuration: 300,
    textStyle: { fontFamily: theme.fontFamily },
    aria: { enabled: true },
    legend: hasCompare
      ? {
          top: 0,
          left: 0,
          itemWidth: 14,
          itemHeight: 8,
          icon: 'roundRect',
          textStyle: { color: theme.textSecondary, fontFamily: theme.fontFamily, fontSize: 12 },
          data: [input.currentLabel ?? 'Текущий период', input.previousLabel ?? 'Период сравнения'],
        }
      : { show: false },
    grid: { top: hasCompare ? 32 : 4, left: 8, right: 8, bottom: 4, outerBoundsMode: 'same', outerBoundsContain: 'axisLabel' } as any,
    tooltip: {
      ...tooltipBase(theme),
      trigger: 'axis',
      axisPointer: { type: 'shadow', shadowStyle: { color: withAlpha(theme.textMuted, 0.08) } },
      formatter: (raw: any) => {
        const params = Array.isArray(raw) ? raw : [raw]
        const index = params[0]?.dataIndex ?? 0
        const step = steps[index]
        const conversion = conversions[index]
        if (!step) return ''
        const delta = relativeDelta(step.value, step.previous)
        const conversionDelta =
          isFiniteNumber(conversion.fromPrevious) && isFiniteNumber(conversion.previousFromPrevious)
            ? conversion.fromPrevious - conversion.previousFromPrevious
            : null
        const footer: string[] = []
        if (conversion.fromPrevious !== null) {
          footer.push(
            `Конверсия из предыдущего шага: ${formatShare(conversion.fromPrevious)}` +
              (conversionDelta !== null ? ` (${conversionDelta >= 0 ? '+' : '−'}${formatShare(Math.abs(conversionDelta)).replace('%', ' п.п.')})` : ''),
          )
        }
        if (conversion.fromFirst !== null && index > 1) footer.push(`Из первого шага: ${formatShare(conversion.fromFirst)}`)
        return tooltipHtml(
          theme,
          step.label,
          [
            { color: colors[index], label: input.currentLabel ?? 'текущий период', value: isFiniteNumber(step.value) ? valueFormat(step.value) : '—', note: delta !== null ? formatSignedShare(delta) : undefined, noteColor: deltaColor(theme, delta) },
            ...(hasCompare
              ? [{ color: colors[index], dashed: true, label: input.previousLabel ?? 'сравнение', value: isFiniteNumber(step.previous) ? valueFormat(step.previous) : '—' }]
              : []),
          ],
          footer,
        )
      },
    },
    xAxis: { type: 'value', show: false, max: (extent: { max: number }) => extent.max * 1.5 },
    yAxis: {
      type: 'category',
      inverse: true,
      data: steps.map((step) => step.label),
      axisLine: { show: true, lineStyle: { color: theme.axis } },
      axisTick: { show: false },
      axisLabel: { ...axisTextStyle(theme), color: theme.textSecondary, fontSize: 12 },
    },
    series,
  } as EChartsOption
}
